import { all, call, put, take, fork, select, SagaReturnType, spawn, race } from "redux-saga/effects";

import { PayloadAction } from "@reduxjs/toolkit";

import { globalHttpService } from "./apiService";

import slice from "../reducer";

import { generateUserInfo } from "../../../auth/utils";

import { MessagesConfig } from "../components/MessageProvider/config";

import { ContactFormInputModel } from "../../ContactModal/components/ContactForm/types";
import { makeSelectBitrixCompanyDetailsById, makeSelectBitrixOptions, makeSelectHost, makeSelectUserInfo } from "../selectors";
import { AuthActions } from "../../../auth/actions";
import { UserInfoDto } from "../../../dto/User/UserInfoDto";
import { SettingsDto } from "../../../dto/Settings/SettingsDto";
import dictionaryCompanySettingsApi from "../../../api/apiServices/dictionary/controllers/companySettings/companySettings.api";
import backOfficeDealsApi from "../../../api/apiServices/backOffice/controllers/deals/deals.api";
import notificationDealNotificationsApi from "../../../api/apiServices/backOffice/controllers/dealQuestions/dealQuestions.api";
import { DealNotificationDataConverter } from "../../../viewModels/Notification/DealNotificationModel";
import dictionaryBitrixCarriersApi from "../../../api/apiServices/dictionary/controllers/bitrixCarriers/bitrixCarriers.api";
import dictionaryBitrixClientsApi from "../../../api/apiServices/dictionary/controllers/bitrixClients/bitrixClients.api";
import { BitrixCompanyDataConverter } from "../../../viewModels/Dictionaries/BitrixCompanyModel";
import { ContractorSettingsDataConverter } from "../../../viewModels/Settings/ContractorModel";
import backOfficeProductUsageEventsApi from "../../../api/apiServices/backOffice/controllers/productUsageEvents/productUsageEvents.api";

function* sendFeedbackFormWatcher() {
  while (true) {
    const { payload }: PayloadAction<ContactFormInputModel> = yield take(slice.actions.sendFeedbackForm.type);
    const { fullname, phoneNumber, email, message } = payload;

    const { success, reject } = yield call([globalHttpService, globalHttpService.SendFeedbackForm], fullname, phoneNumber, email, message);

    if (success) {
      yield put(slice.actions.sendFeedbackFormSuccess());
      yield put(slice.actions.setMessageConfigShow(MessagesConfig.sendFeedbackFormSuccess));
    }

    if (reject) {
      yield put(slice.actions.sendFeedbackFormError());
    }
  }
}

function* recordProductUsageEvent(payload: ReturnType<typeof slice.actions.recordProductUsageEvent>["payload"]) {
  try {
    const { success, reject }: SagaReturnType<typeof backOfficeProductUsageEventsApi.record> = yield call(
      [backOfficeProductUsageEventsApi, backOfficeProductUsageEventsApi.record],
      payload,
    );

    if (success) {
      yield put(slice.actions.recordProductUsageEventSuccess());
    }

    if (reject) {
      yield put(slice.actions.recordProductUsageEventReject());
    }
  } catch {
    yield put(slice.actions.recordProductUsageEventReject());
  }
}

function* recordProductUsageEventWatcher() {
  while (true) {
    const { payload }: ReturnType<typeof slice.actions.recordProductUsageEvent> = yield take(slice.actions.recordProductUsageEvent.type);
    yield fork(recordProductUsageEvent, payload);
  }
}

function* sendDealNotificationResponseWatcher() {
  while (true) {
    const { payload }: ReturnType<typeof slice.actions.sendDealNotificationResponse> = yield take(
      slice.actions.sendDealNotificationResponse.type,
    );

    const { success, reject }: SagaReturnType<typeof notificationDealNotificationsApi.changeStatus> = yield call(
      [notificationDealNotificationsApi, notificationDealNotificationsApi.changeStatus],
      { id: payload.id, status: "RESPONDED", response: payload.response, officeType: payload.officeType },
    );

    if (success) {
      yield put(slice.actions.sendDealNotificationResponseSuccess(DealNotificationDataConverter.convertDealNotification(success.payload)));
    }

    if (reject) {
      yield put(slice.actions.sendDealNotificationResponseReject());
    }
  }
}

function* closeDealNotificationWatcher() {
  while (true) {
    const { payload }: ReturnType<typeof slice.actions.closeDealNotification> = yield take(slice.actions.closeDealNotification.type);

    const { success, reject }: SagaReturnType<typeof notificationDealNotificationsApi.changeStatus> = yield call(
      [notificationDealNotificationsApi, notificationDealNotificationsApi.changeStatus],
      { id: payload.id, officeType: payload.officeType, status: "CLOSED" },
    );

    if (success) {
      yield put(slice.actions.closeDealNotificationSuccess(DealNotificationDataConverter.convertDealNotification(success.payload)));
    }

    if (reject) {
      yield put(slice.actions.closeDealNotificationReject());
    }
  }
}

function* changeContainerIssuingAdditionalJobWatcher() {
  while (true) {
    const { payload }: ReturnType<typeof slice.actions.changeContainerIssuingAdditionalJob> = yield take(
      slice.actions.changeContainerIssuingAdditionalJob.type,
    );

    yield call([globalHttpService, globalHttpService.ChangeContainerIssuingAdditionalJobWatcher], payload);
  }
}

function* loadUserSettingsWorker() {
  try {
    const [{ success: userSettingsSuccess }]: [SagaReturnType<typeof dictionaryCompanySettingsApi.get>] = yield all([
      call([dictionaryCompanySettingsApi, dictionaryCompanySettingsApi.get]),
    ]);

    if (!userSettingsSuccess) {
      yield put(slice.actions.initialDataLoadedError());
      return;
    }

    yield put(
      slice.actions.userSettingsLoaded({
        settings: {
          ...userSettingsSuccess.payload,
          clients: ContractorSettingsDataConverter.convertContractors(userSettingsSuccess.payload.clients ?? []),
          carriers: ContractorSettingsDataConverter.convertContractors(userSettingsSuccess.payload.carriers ?? []),
        },
      }),
    );
  } catch {
    yield put(slice.actions.initialDataLoadedError());
  } finally {
    yield put(slice.actions.userDataLoaded());
  }
}

function* loadGlobalSettingsWorker() {
  const { success: settingsSuccess, reject: settingsError }: { success: { payload: SettingsDto }; reject: unknown } = yield call([
    globalHttpService,
    globalHttpService.GetSettings,
  ]);

  if (settingsSuccess) {
    yield put(
      slice.actions.globalSettingsLoaded({
        ...settingsSuccess.payload,
        leadStatuses: settingsSuccess.payload.leadStatusDtoList,
        transportationStages: settingsSuccess.payload.transportationStageDtos,
      }),
    );
    yield put(slice.actions.initialDataLoaded());
  }

  if (settingsError) {
    yield put(slice.actions.initialDataLoadedError());
  }
}

function* loadPendingFillDeals() {
  const { success }: SagaReturnType<typeof backOfficeDealsApi.getPendingFill> = yield call([
    backOfficeDealsApi,
    backOfficeDealsApi.getPendingFill,
  ]);

  if (success) {
    yield put(slice.actions.setPendingFillDeals(success.payload));
  }
}

function* loadBitrixCarriers() {
  const { success, reject }: SagaReturnType<typeof dictionaryBitrixCarriersApi.basicInfo> = yield call([
    dictionaryBitrixCarriersApi,
    dictionaryBitrixCarriersApi.basicInfo,
  ]);

  if (success) {
    yield put(slice.actions.loadBitrixCarriersSucces(success.payload));
  }

  if (reject) {
    yield put(slice.actions.loadBitrixCarriersReject());
  }
}

function* loadRegularBitrixCompaniesWorker(abortController: AbortController) {
  try {
    const { success }: SagaReturnType<typeof dictionaryBitrixClientsApi.regular> = yield call(
      [dictionaryBitrixClientsApi, dictionaryBitrixClientsApi.regular],
      abortController,
    );

    if (success) {
      yield put(slice.actions.loadRegularBitrixCompaniesSuccess(BitrixCompanyDataConverter.convertContractors(success.payload)));
    } else {
      yield put(slice.actions.loadRegularBitrixCompaniesReject());
    }
  } catch {
    yield put(slice.actions.loadRegularBitrixCompaniesReject());
  }
}

function* loadRegularBitrixCompaniesWatcher() {
  while (true) {
    yield take(slice.actions.loadRegularBitrixCompanies.type);
    const abortController = new AbortController();

    try {
      yield race({
        request: call(loadRegularBitrixCompaniesWorker, abortController),
        logout: take(AuthActions.logoutSuccess.type),
      });
    } finally {
      abortController.abort();
    }
  }
}

function* loadBitrixCompanyDetailsWorker(id: number) {
  const cached: ReturnType<ReturnType<typeof makeSelectBitrixCompanyDetailsById>> = yield select(makeSelectBitrixCompanyDetailsById(id));

  if (cached) {
    return;
  }

  const { success, reject }: SagaReturnType<typeof dictionaryBitrixClientsApi.getById> = yield call(
    [dictionaryBitrixClientsApi, dictionaryBitrixClientsApi.getById],
    id,
  );

  if (success) {
    yield put(
      slice.actions.loadBitrixCompanyDetailsSuccess({
        id,
        company: BitrixCompanyDataConverter.convertContractor(success.payload),
      }),
    );
  }

  if (reject) {
    yield put(slice.actions.loadBitrixCompanyDetailsReject(id));
  }
}

function* loadBitrixCompanyDetailsWatcher() {
  while (true) {
    const { payload }: ReturnType<typeof slice.actions.loadBitrixCompanyDetails> = yield take(slice.actions.loadBitrixCompanyDetails.type);

    yield fork(loadBitrixCompanyDetailsWorker, payload);
  }
}

// Вопросы по сделкам НАМ (грузим для всех пользователей, можем быть ответвенным в какой-то сделке)
function* loadDealNotificationRequestsSentToUs() {
  const { success }: SagaReturnType<typeof notificationDealNotificationsApi.getAll> = yield call(
    [notificationDealNotificationsApi, notificationDealNotificationsApi.getAll],
    { status: "REQUESTED", sent: false },
  );

  if (success) {
    yield put(slice.actions.setDealsNotificationsToUs(DealNotificationDataConverter.convertDealNotifications(success.payload)));
  }
}

// Вопросы по сделкам от нас (только для боссов и админов)
function* loadDealNotificationRequestsSentByUs() {
  const userInfo: ReturnType<ReturnType<typeof makeSelectUserInfo>> = yield select(makeSelectUserInfo());

  if (userInfo.role.isBoss || userInfo.role.userType === "ADMINISTRATOR") {
    const [{ success: requestedSuccess }, { success: respondedSuccess }]: [
      SagaReturnType<typeof notificationDealNotificationsApi.getAll>,
      SagaReturnType<typeof notificationDealNotificationsApi.getAll>,
    ] = yield all([
      call([notificationDealNotificationsApi, notificationDealNotificationsApi.getAll], { sent: true, status: "REQUESTED" }),
      call([notificationDealNotificationsApi, notificationDealNotificationsApi.getAll], { sent: true, status: "RESPONDED" }),
    ]);

    if (requestedSuccess && respondedSuccess) {
      yield put(
        slice.actions.setDealsNotificationsByUs({
          requests: DealNotificationDataConverter.convertDealNotifications(requestedSuccess.payload),
          responses: DealNotificationDataConverter.convertDealNotifications(respondedSuccess.payload),
        }),
      );
    }
  }
}

function* loginSuccessWatcher() {
  while (true) {
    yield take(AuthActions.loginSuccess.type);

    yield put(slice.actions.loadRegularBitrixCompanies());
    yield spawn(loadPendingFillDeals);
    yield spawn(loadDealNotificationRequestsSentToUs);
    yield spawn(loadDealNotificationRequestsSentByUs);
    yield call(loadUserSettingsWorker);
  }
}

export default function* globalSaga() {
  yield fork(loadRegularBitrixCompaniesWatcher);
  const host: ReturnType<ReturnType<typeof makeSelectHost>> = yield select(makeSelectHost());
  const bitrixOptions: ReturnType<ReturnType<typeof makeSelectBitrixOptions>> = yield select(makeSelectBitrixOptions());

  const {
    success: userSuccess,
    reject: userReject,
  }: { success?: { payload: UserInfoDto }; reject?: { payload: { httpCode?: number; code?: string } } } =
    host === "BITRIX" && bitrixOptions
      ? yield call([globalHttpService, globalHttpService.LoginBitrix], bitrixOptions.email)
      : yield call([globalHttpService, globalHttpService.GetUser]);

  if (userSuccess) {
    const userInfo = generateUserInfo(userSuccess.payload);
    yield put(slice.actions.userLoaded(userInfo));
    yield put(slice.actions.loadRegularBitrixCompanies());
    yield spawn(loadPendingFillDeals);
    yield spawn(loadDealNotificationRequestsSentToUs);
    yield spawn(loadDealNotificationRequestsSentByUs);
    yield spawn(loadBitrixCarriers);
    // сначала загружаем только настройки компании
    yield call(loadUserSettingsWorker);
  }

  // тут загружаем уже все остальные справочники и завершаем загрузку
  if (userReject && userReject.payload.httpCode !== 401) {
    yield put(slice.actions.initialDataLoadedError());
  } else {
    yield call(loadGlobalSettingsWorker);
  }

  yield fork(loginSuccessWatcher);
  yield fork(sendFeedbackFormWatcher);
  yield fork(recordProductUsageEventWatcher);
  yield fork(changeContainerIssuingAdditionalJobWatcher);
  yield fork(sendDealNotificationResponseWatcher);
  yield fork(closeDealNotificationWatcher);
  yield fork(loadBitrixCompanyDetailsWatcher);
}
